// src/pages/AnalyticsDashboard.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Grid,
  Card,
  CardContent,
  Stack,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Chip,
  Avatar,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
} from '@mui/material';
import PeopleIcon from '@mui/icons-material/People';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import ArticleIcon from '@mui/icons-material/Article';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import CommentIcon from '@mui/icons-material/Comment';
import FavoriteIcon from '@mui/icons-material/Favorite';
import ShareIcon from '@mui/icons-material/Share';
import VisibilityIcon from '@mui/icons-material/Visibility';
import RefreshIcon from '@mui/icons-material/Refresh';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  LineChart,
  Line,
} from 'recharts';
import {
  getAdminOverview,
  getEngagementAnalytics,
} from '../services/adminService';
import {
  AdminOverviewResponse,
  EngagementAnalyticsResponse,
} from '../types/admin';
import UserAnalyticsTab from '../components/admin/UserAnalyticsTab';
import { useApiError } from '../hooks/useApiError';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <Box role="tabpanel" hidden={value !== index} sx={{ py: 3 }}>
    {value === index && children}
  </Box>
);

// Color palette for charts
const COLORS = ['#2563eb', '#7c3aed', '#16a34a', '#d97706', '#dc2626', '#0891b2', '#4f46e5', '#059669'];

const AnalyticsDashboard: React.FC = () => {
  const { t } = useTranslation();
  const { handleError } = useApiError();
  const [tabValue, setTabValue] = useState(0);
  const [period, setPeriod] = useState(30);
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState<AdminOverviewResponse | null>(null);
  const [engagement, setEngagement] = useState<EngagementAnalyticsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const [overviewRes, engagementRes] = await Promise.all([
        getAdminOverview(),
        getEngagementAnalytics(period),
      ]);
      setOverview(overviewRes);
      setEngagement(engagementRes);
    } catch (err: any) {
      console.error('Failed to fetch analytics:', err);
      setError(err.response?.data?.detail || t('common.error'));
      handleError(err);
    } finally {
      setLoading(false);
    }
  }, [period, t, handleError]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handlePeriodChange = (_: React.MouseEvent<HTMLElement>, newPeriod: number | null) => {
    if (newPeriod !== null) {
      setPeriod(newPeriod);
    }
  };

  if (loading && !overview) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  if (error && !overview) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} justifyContent="space-between" alignItems={{ md: 'center' }} spacing={2} sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} color="#1e293b">
            {t('analytics.title', 'Analytics Dashboard')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('analytics.subtitle', 'Vue d\'ensemble des performances de la plateforme')}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2} alignItems="center">
          <ToggleButtonGroup
            value={period}
            exclusive
            onChange={handlePeriodChange}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid #e2e8f0',
                '&.Mui-selected': { bgcolor: '#2563eb', color: 'white', '&:hover': { bgcolor: '#1d4ed8' } },
              },
            }}
          >
            <ToggleButton value={7}>7j</ToggleButton>
            <ToggleButton value={30}>30j</ToggleButton>
            <ToggleButton value={90}>90j</ToggleButton>
          </ToggleButtonGroup>
          <Tooltip title={t('common.refresh', 'Actualiser')}>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 500,
              minHeight: 48,
            },
          }}
        >
          <Tab icon={<VisibilityIcon />} iconPosition="start" label={t('analytics.tabs.overview', 'Vue d\'ensemble')} />
          <Tab icon={<PeopleIcon />} iconPosition="start" label={t('analytics.tabs.users', 'Utilisateurs')} />
          <Tab icon={<ArticleIcon />} iconPosition="start" label={t('analytics.tabs.engagement', 'Engagement')} />
        </Tabs>
      </Box>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        <OverviewTab overview={overview} engagement={engagement} loading={loading} />
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <UserAnalyticsTab />
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <EngagementTab engagement={engagement} loading={loading} />
      </TabPanel>
    </Box>
  );
};

// ===== OVERVIEW TAB =====
interface OverviewTabProps {
  overview: AdminOverviewResponse | null;
  engagement: EngagementAnalyticsResponse | null;
  loading: boolean;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ overview, engagement, loading }) => {
  const { t } = useTranslation();

  if (!overview) return null;

  const statsCards = [
    {
      title: t('analytics.totalUsers', 'Utilisateurs'),
      value: overview.users.total,
      change: overview.users.growth_rate_7d,
      icon: <PeopleIcon />,
      color: '#2563eb',
      bgColor: '#dbeafe',
    },
    {
      title: t('analytics.verifiedProfiles', 'Profils vérifiés'),
      value: overview.users.verified || 0,
      subtitle: `${overview.users.active} ${t('analytics.active', 'actifs')}`,
      icon: <VerifiedUserIcon />,
      color: '#9333EA',
      bgColor: '#f3e8ff',
    },
    {
      title: t('analytics.activeUsers', 'Utilisateurs actifs'),
      value: overview.users.active || 0,
      subtitle: `${((overview.users.active / overview.users.total) * 100).toFixed(1)}%`,
      icon: <TrendingUpIcon />,
      color: '#16a34a',
      bgColor: '#dcfce7',
    },
    {
      title: t('analytics.bannedUsers', 'Utilisateurs bannis'),
      value: overview.users.banned || 0,
      icon: <ArticleIcon />,
      color: '#d97706',
      bgColor: '#fef3c7',
    },
  ];

  // Prepare pie chart data for user roles
  const roleData = [
    { name: 'Users', value: overview.users.by_role.user, color: '#2563eb' },
    { name: 'Admins', value: overview.users.by_role.admin, color: '#7c3aed' },
    { name: 'Super Admins', value: overview.users.by_role.super_admin, color: '#d97706' },
  ];

  // Content activity data
  const contentData = [
    { name: 'Posts', value: overview.content.posts.total, today: overview.content.posts.today },
    { name: 'Comments', value: overview.content.comments.total, today: overview.content.comments.today },
    { name: 'Reactions', value: overview.content.reactions.total, today: overview.content.reactions.today },
  ];

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        {statsCards.map((stat, index) => (
          <Grid item xs={6} md={3} key={index}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
              <CardContent sx={{ p: 2.5 }}>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="body2" color="text.secondary" fontWeight={500}>
                      {stat.title}
                    </Typography>
                    <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                      {stat.value.toLocaleString()}
                    </Typography>
                    {stat.change && (
                      <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mt: 0.5 }}>
                        {parseFloat(stat.change) >= 0 ? (
                          <TrendingUpIcon sx={{ fontSize: 16, color: '#16a34a' }} />
                        ) : (
                          <TrendingDownIcon sx={{ fontSize: 16, color: '#dc2626' }} />
                        )}
                        <Typography variant="caption" color={parseFloat(stat.change) >= 0 ? '#16a34a' : '#dc2626'} fontWeight={600}>
                          {stat.change}
                        </Typography>
                      </Stack>
                    )}
                    {stat.subtitle && (
                      <Typography variant="caption" color="text.secondary">
                        {stat.subtitle}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ p: 1.5, bgcolor: stat.bgColor, borderRadius: 2 }}>
                    {React.cloneElement(stat.icon, { sx: { color: stat.color, fontSize: 24 } })}
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Grid container spacing={3}>
        {/* User Role Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.userDistribution', 'Répartition des utilisateurs')}
              </Typography>
              <Box sx={{ width: '100%', height: 250 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={roleData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={90}
                      paddingAngle={2}
                      dataKey="value"
                    >
                      {roleData.map((entry, index) => (
                        <Cell key={index} fill={entry.color} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                      formatter={(value: number) => [value.toLocaleString(), '']}
                    />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Content Activity */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.contentActivity', 'Activité du contenu')}
              </Typography>
              <Stack spacing={3} sx={{ mt: 2 }}>
                {contentData.map((item, index) => (
                  <Box key={index}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                      <Typography variant="body2" fontWeight={500}>{item.name}</Typography>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="h6" fontWeight={700}>{item.value.toLocaleString()}</Typography>
                        <Chip
                          size="small"
                          label={`+${item.today}`}
                          sx={{ bgcolor: '#dcfce7', color: '#16a34a', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      </Stack>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((item.value / Math.max(...contentData.map(d => d.value))) * 100, 100)}
                      sx={{ height: 8, borderRadius: 1, bgcolor: '#f1f5f9', '& .MuiLinearProgress-bar': { bgcolor: COLORS[index], borderRadius: 1 } }}
                    />
                  </Box>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Engagement Metrics */}
        <Grid item xs={12} md={4}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.engagementMetrics', 'Métriques d\'engagement')}
              </Typography>
              <Stack spacing={2} sx={{ mt: 2 }}>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.engagementRate', 'Taux d\'engagement')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#2563eb">{overview.engagement.engagement_rate}</Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.activeUsers7d', 'Utilisateurs actifs (7j)')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#16a34a">{overview.engagement.active_users_7d}</Typography>
                  </Stack>
                </Box>
                <Box sx={{ p: 2, bgcolor: '#f8fafc', borderRadius: 2 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="body2" color="text.secondary">{t('analytics.avgPostsPerDay', 'Moy. posts/jour')}</Typography>
                    <Typography variant="h5" fontWeight={700} color="#7c3aed">{overview.engagement.avg_posts_per_day.toFixed(1)}</Typography>
                  </Stack>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Alerts */}
        {overview.alerts && overview.alerts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #fef3c7', boxShadow: 'none', bgcolor: '#fffbeb' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#92400e" gutterBottom>
                  {t('analytics.alerts', 'Alertes')} ({overview.alerts_count})
                </Typography>
                <Stack spacing={1}>
                  {overview.alerts.slice(0, 5).map((alert, index) => (
                    <Alert key={index} severity={alert.type === 'error' ? 'error' : alert.type === 'warning' ? 'warning' : 'info'}>
                      <Typography variant="body2">{alert.message}</Typography>
                    </Alert>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

// ===== ENGAGEMENT TAB =====
interface EngagementTabProps {
  engagement: EngagementAnalyticsResponse | null;
  loading: boolean;
}

const EngagementTab: React.FC<EngagementTabProps> = ({ engagement, loading }) => {
  const { t } = useTranslation();

  if (loading || !engagement) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  // Format chart data
  const chartData = engagement.charts.daily_posts.map(item => ({
    date: new Date(item.date).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' }),
    posts: item.count,
  }));

  // Reaction types data
  const reactionData = Object.entries(engagement.reactions.by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  // Post types data
  const postTypeData = Object.entries(engagement.posts.by_type).map(([type, count]) => ({
    name: type.charAt(0).toUpperCase() + type.slice(1),
    value: count,
  }));

  return (
    <Box>
      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.postsInPeriod', 'Posts (période)')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.posts.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.posts.avg_per_day.toFixed(1)}/jour
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dbeafe', borderRadius: 2 }}>
                  <ArticleIcon sx={{ color: '#2563eb', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.comments', 'Commentaires')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.comments.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.comments.avg_per_post.toFixed(1)}/post
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#f3e8ff', borderRadius: 2 }}>
                  <CommentIcon sx={{ color: '#7c3aed', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.reactions', 'Réactions')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.reactions.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.reactions.avg_per_post.toFixed(1)}/post
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#fef2f2', borderRadius: 2 }}>
                  <FavoriteIcon sx={{ color: '#dc2626', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} md={3}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
            <CardContent sx={{ p: 2.5 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography variant="body2" color="text.secondary" fontWeight={500}>
                    {t('analytics.reposts', 'Reposts')}
                  </Typography>
                  <Typography variant="h4" fontWeight={700} color="#1e293b" sx={{ mt: 0.5 }}>
                    {engagement.reposts.in_period.toLocaleString()}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {engagement.reposts.avg_per_day.toFixed(1)}/jour
                  </Typography>
                </Box>
                <Box sx={{ p: 1.5, bgcolor: '#dcfce7', borderRadius: 2 }}>
                  <ShareIcon sx={{ color: '#16a34a', fontSize: 24 }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Daily Posts Chart */}
        {chartData.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('analytics.dailyPostsChart', 'Posts quotidiens')}
                </Typography>
                <Box sx={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                          <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                      <XAxis dataKey="date" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <YAxis tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                      <RechartsTooltip
                        contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                        labelStyle={{ fontWeight: 600, color: '#1e293b' }}
                      />
                      <Area type="monotone" dataKey="posts" name="Posts" stroke="#2563eb" strokeWidth={2} fillOpacity={1} fill="url(#colorPosts)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Reaction Types */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.reactionTypes', 'Types de réactions')}
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={reactionData}
                      cx="50%"
                      cy="50%"
                      outerRadius={100}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                    >
                      {reactionData.map((_, index) => (
                        <Cell key={index} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Post Types */}
        <Grid item xs={12} md={6}>
          <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none', height: '100%' }}>
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                {t('analytics.postTypes', 'Types de posts')}
              </Typography>
              <Box sx={{ width: '100%', height: 280 }}>
                <ResponsiveContainer>
                  <BarChart data={postTypeData} layout="vertical" margin={{ top: 5, right: 30, left: 80, bottom: 5 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={true} vertical={false} />
                    <XAxis type="number" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={{ stroke: '#e2e8f0' }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 12, fill: '#64748b' }} tickLine={false} axisLine={false} width={80} />
                    <RechartsTooltip
                      contentStyle={{ backgroundColor: 'white', border: '1px solid #e2e8f0', borderRadius: 8 }}
                    />
                    <Bar dataKey="value" fill="#7c3aed" radius={[0, 4, 4, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Posts */}
        {engagement.top_posts && engagement.top_posts.length > 0 && (
          <Grid item xs={12}>
            <Card sx={{ border: '1px solid #e2e8f0', boxShadow: 'none' }}>
              <CardContent sx={{ p: 3 }}>
                <Typography variant="h6" fontWeight={600} color="#1e293b" gutterBottom>
                  {t('analytics.topPosts', 'Top Posts')}
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('analytics.postTitle', 'Titre')}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{t('analytics.author', 'Auteur')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.views', 'Vues')}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>{t('analytics.shares', 'Partages')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {engagement.top_posts.map((post, index) => (
                        <TableRow key={post.id} sx={{ '&:hover': { bgcolor: '#f8fafc' } }}>
                          <TableCell>{index + 1}</TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight={500} noWrap sx={{ maxWidth: 300 }}>
                              {post.title || 'Sans titre'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Stack direction="row" alignItems="center" spacing={1}>
                              <Avatar sx={{ width: 24, height: 24, fontSize: 12 }}>{post.author?.charAt(0)}</Avatar>
                              <Typography variant="body2">{post.author}</Typography>
                            </Stack>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="#2563eb">
                              {post.views?.toLocaleString() || 0}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" fontWeight={600} color="#7c3aed">
                              {post.shares?.toLocaleString() || 0}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default AnalyticsDashboard;
